import { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../components";
import { FilterPopover } from "../../../components/filterPopover/FilterPopover";
import { Tag } from "../../../components/tag/Tag";
import { SlideTransition } from "../../../components/transition/SlideTransition";
import {
  abvFilterType,
  dietaryTypes,
} from "../../discovery/v2/DiscoveryV2SearchStore";
import {
  SpotlightContainer,
  useSpotlightFilterStore,
} from "../SpotlightFilterStore";
import { MoreSpotlightFilters } from "./MoreSpotlightFilters";
import { Button, H6, Switch } from "@sellar-io/components";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";

interface FilterContainerCardProps {
  onClick?: () => void;
  label?: string;
  children?: ReactNode;
  sticky?: boolean;
  mobileBorder?: boolean;
  desktopClassName?: string;
  className?: string;
  includeActiveToggle?: boolean;
  moreFilters?: ReactNode;
}

const spotlightContainerTypes = [
  "keg",
  "cask",
  "can",
  "bottle",
  "bagInBox",
] as const;

export const SpotlightFilterContainerCard = ({
  onClick,
  label,
  children,
  sticky = true,
  mobileBorder = false,
  desktopClassName,
  className,
  includeActiveToggle = false,
  moreFilters,
}: FilterContainerCardProps) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [mobileFilterActive, setMobileFilterActive] = useState(false);
  const [clickOutside, setClickOutside] = useState<boolean>(true);
  const state = useSpotlightFilterStore(useShallow((state) => state));
  const toggleContainerType = useSpotlightFilterStore(
    (state) => state.toggleContainerType,
  );

  const handleToggle = (type: SpotlightContainer) => {
    toggleContainerType(type);
    if (onClick) {
      onClick();
    }
  };

  const additionalFilters = (
    <>
      {includeActiveToggle && (
        <>
          <div className="flex w-full items-center justify-between gap-2">
            <span>Only show Active products</span>
            <Switch
              checked={state.activeOnly}
              onChange={(checked) => {
                state.setActiveOnly(checked);
                state.setInactiveOnly(false);
              }}
            />
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <span>Only show Inactive products</span>
            <Switch
              checked={state.inactiveOnly}
              onChange={(checked) => {
                state.setInactiveOnly(checked);
                state.setActiveOnly(false);
              }}
            />
          </div>
        </>
      )}
      {moreFilters}
    </>
  );

  return (
    <FloatingLayerProvider host={sticky ? "pinned" : "content"}>
      <div
        className={twMerge(
          "flex flex-col",
          sticky && `sticky top-[var(--nav-height)] ${zLayer.pinned}`,
          isDesktop && "bg-zinc-100",
          !isDesktop && "overflow-hidden",
          isDesktop && desktopClassName,
        )}
      >
        <div
          className={twMerge(
            "@container flex flex-col gap-2 rounded-lg border-b border-zinc-300 bg-zinc-50 p-4",
            (isDesktop || mobileBorder) && "border border-zinc-300",
            className,
          )}
        >
          <div className="flex flex-col items-start justify-between gap-2 @2xl:flex-row">
            <div className="flex flex-wrap items-center gap-2">
              {label && <H6>{label}</H6>}
              {spotlightContainerTypes.map((containerType) => {
                const isSelected =
                  state.selectedContainerTypes.includes(containerType);
                return (
                  <Button
                    color="slim"
                    size="small"
                    key={containerType}
                    onClick={() => handleToggle(containerType)}
                    selected={isSelected}
                    aria-pressed={isSelected}
                    className="capitalize shadow-xs"
                    aria-label={`Filter by ${containerType}`}
                  >
                    {containerType === "bagInBox"
                      ? "Bag In Box"
                      : containerType.charAt(0).toUpperCase() +
                        containerType.slice(1)}
                  </Button>
                );
              })}
              {!isDesktop ? (
                <Button
                  onClick={() => setMobileFilterActive(!mobileFilterActive)}
                  color="slim"
                  size="small"
                  className={twMerge(
                    "shadow-xs",
                    mobileFilterActive && "bg-zinc-200",
                  )}
                >
                  <Icon icon="FILTER_VARIANT" />
                  More filters
                </Button>
              ) : (
                <FilterPopover
                  icon="FILTER_VARIANT"
                  buttonText="More filters"
                  compact={false}
                  active={filterActive}
                  placement={"bottom"}
                  toggleActive={setFilterActive}
                  clickOutside={clickOutside}
                  buttonClassName="shadow-xs rounded-md"
                >
                  <MoreSpotlightFilters
                    onDismiss={() => setFilterActive(false)}
                    setClickOutside={(value?: boolean) =>
                      setClickOutside(value ?? !clickOutside)
                    }
                  >
                    {additionalFilters}
                  </MoreSpotlightFilters>
                </FilterPopover>
              )}
            </div>
            {children}
          </div>
          {(state.selectedAbvTypes.length > 0 ||
            state.selectedDietaryTypes.length > 0) && (
            <div className="flex w-full flex-row items-center gap-1 overflow-x-auto">
              <>
                {state.selectedAbvTypes.length > 0 &&
                  state.selectedAbvTypes.map((abvType, i) => (
                    <Tag
                      key={i}
                      className="mr-2"
                      onClick={() =>
                        state.setAbvType(
                          state.selectedAbvTypes.filter(
                            (abvTypeSelected) => abvTypeSelected !== abvType,
                          ),
                        )
                      }
                    >
                      {abvFilterType[abvType].name}
                    </Tag>
                  ))}
                {state.selectedDietaryTypes.length > 0 &&
                  state.selectedDietaryTypes.map((selectedDietaryType, i) => (
                    <Tag
                      key={i}
                      className="mr-2"
                      onClick={() =>
                        state.setDietaryType(
                          state.selectedDietaryTypes.filter(
                            (dietaryType) =>
                              dietaryType !== selectedDietaryType,
                          ),
                        )
                      }
                    >
                      {dietaryTypes[selectedDietaryType]}
                    </Tag>
                  ))}
              </>
            </div>
          )}
        </div>
        <SlideTransition
          show={mobileFilterActive}
          className={"w-full"}
          parentClass="w-full bg-white"
        >
          <MoreSpotlightFilters onDismiss={() => setMobileFilterActive(false)}>
            {additionalFilters}
          </MoreSpotlightFilters>
        </SlideTransition>
      </div>
    </FloatingLayerProvider>
  );
};
